export const PLAYER_FILE_TYPES = {
    video: "video",
    audio: "audio",
    file: "file",
    pdf: "pdf",
    google: "google",
    embed: "embed",
    text: "text",
    header: "header",
    image: "image",
};

export const PLAYER_VIDEO_SOURCES = {
    youtube: "youtube",
    vimeo: "vimeo",
    wistia: "wistia",
    other: "other",
};

export const PLAYER_HEADER_TYPES = {
    button: "button",
    link: "link",
    text: "text",
};

export const PLAYER_HEADER_SIDES = {
    left: "left",
    right: "right",
};

export const PLAYER_EXAMPLE_TEXT_EN = `# Lesson: How to Create a Course

Welcome to this lesson on how to create an effective and engaging course. Whether you're designing a course for online learners, corporate training, or traditional classroom settings, this guide will walk you through the key steps.

---

## Objectives

By the end of this lesson, you will be able to:
1. Identify the key steps in course creation.
2. Understand how to define learning outcomes.
3. Develop a basic course structure and content plan.

---

## Step 1: Define Your Audience and Goals

Before creating your course, answer these essential questions:
- **Who is your target audience?**
  - Age, professional background, experience level, etc.
- **What are the primary goals of your course?**
  - What knowledge, skills, or attitudes should learners gain?

**Example:**  
If you're creating a course on "Basic Web Development," your target audience might be beginners interested in coding, and your goal could be to teach them how to build a simple website using HTML and CSS.

---

## Step 2: Outline Learning Outcomes

Learning outcomes are clear, measurable statements about what students will achieve.  
**Tip:** Use action verbs like *explain*, *demonstrate*, *design*, or *create*.

**Example of Learning Outcomes:**
- By the end of this course, learners will be able to:
  1. Explain the fundamental concepts of HTML and CSS.
  2. Design a basic webpage structure.
  3. Create and style a webpage using HTML and CSS.

---

## Step 3: Develop the Course Structure

Break down your course into smaller sections or modules.  
Each module should focus on a specific topic and build on the previous ones.

**Example Course Structure:**
1. **Introduction to Web Development**
   - Overview of web technologies.
2. **HTML Basics**
   - Tags, elements, and basic structure.
3. **CSS Basics**
   - Styling with CSS: colors, fonts, layouts.
4. **Building Your First Website**
   - Combining HTML and CSS.

---

## Step 4: Create Engaging Content

Use a mix of content types to cater to different learning styles:
- **Text and Reading Materials**
  - Detailed explanations, examples, and additional resources.
- **Visuals**
  - Diagrams, infographics, and videos.
- **Interactive Activities**
  - Quizzes, coding challenges, and discussion prompts.

**Tip:** Keep content concise and focused. Aim to engage learners without overwhelming them.

---

## Step 5: Test and Refine

Before launching your course, test it with a small group:
- Gather feedback on content clarity, pacing, and engagement.
- Make adjustments based on learner feedback.

**Example Checklist:**
- Are the learning outcomes clear and measurable?
- Does the course structure flow logically?
- Are the activities and assessments aligned with the outcomes?

---

## Summary

Creating a course involves thoughtful planning and execution. By defining your audience, setting clear outcomes, structuring your content, and refining based on feedback, you can design a course that meets your learners' needs.

---

### Activity: Draft Your Course Plan

1. Define your course topic and target audience.
2. Write three learning outcomes.
3. Create a basic outline of your course structure.

Good luck!
`;

export const PLAYER_EXAMPLE_DESC_EN = `- Learn how to **design an engaging and effective course** from start to finish.  
- This lesson covers:  
  - Defining your audience.  
  - Setting clear learning outcomes.  
  - Structuring and organizing content.  
  - Refining your course based on feedback.  
- Perfect for:  
  - Educators, trainers, and anyone interested in creating impactful learning experiences.
`;

export const PLAYER_EXAMPLE_TEXT_PL = `# Lekcja: Jak stworzyć kurs

Witamy na lekcji dotyczącej tworzenia skutecznych i angażujących kursów. Niezależnie od tego, czy projektujesz kurs online, szkolenie firmowe, czy zajęcia w tradycyjnej klasie, ten przewodnik przeprowadzi Cię przez kluczowe kroki.

---

## Cele

Po ukończeniu tej lekcji będziesz w stanie:
1. Zidentyfikować kluczowe kroki w tworzeniu kursu.
2. Zrozumieć, jak definiować cele nauczania.
3. Opracować podstawową strukturę kursu i plan treści.

---

## Krok 1: Zdefiniuj swoją grupę docelową i cele

Przed stworzeniem kursu odpowiedz na kilka podstawowych pytań:
- **Kto jest Twoją grupą docelową?**
  - Wiek, doświadczenie zawodowe, poziom wiedzy itp.
- **Jakie są główne cele Twojego kursu?**
  - Jaką wiedzę, umiejętności lub postawy powinni zdobyć uczestnicy?

**Przykład:**  
Jeśli tworzysz kurs pt. „Podstawy tworzenia stron internetowych”, Twoją grupą docelową mogą być początkujący zainteresowani programowaniem, a celem może być nauczenie ich budowy prostych stron internetowych za pomocą HTML i CSS.

---

## Krok 2: Określ cele nauczania

Cele nauczania to jasne, mierzalne stwierdzenia opisujące, co uczestnicy osiągną.  
**Porada:** Używaj czasowników operacyjnych, takich jak *wyjaśnij*, *zademonstruj*, *zaprojektuj* lub *stwórz*.

**Przykład celów nauczania:**
- Po ukończeniu tego kursu uczestnicy będą w stanie:
  1. Wyjaśnić podstawowe pojęcia związane z HTML i CSS.
  2. Zaprojektować podstawową strukturę strony internetowej.
  3. Stworzyć i sformatować stronę internetową za pomocą HTML i
`;

export const PLAYER_EXAMPLE_DESC_PL = `- Dowiedz się, jak **zaprojektować angażujący i skuteczny kurs** od podstaw.  
- Lekcja obejmuje:  
  - Określenie grupy docelowej.  
  - Wyznaczanie jasnych celów nauczania.  
  - Strukturyzowanie i organizowanie treści.  
  - Ulepszanie kursu na podstawie opinii.  
- Idealne dla:  
  - Edukatorów, trenerów i wszystkich zainteresowanych tworzeniem wartościowych doświadczeń edukacyjnych.  `;
